import { motion } from "framer-motion";
import { Radio, Waves, Zap, Gauge } from "lucide-react";

const ICONS = {
  "01": Radio,
  "02": Waves,
  "03": Zap,
  "04": Gauge,
};

export default function Partnership({ data }) {
  return (
    <section
      id="xbox"
      data-testid="partnership-section"
      className="relative py-24 md:py-40 px-6 md:px-12 lg:px-16"
    >
      <div className="max-w-[1600px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mb-14 md:mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="w-12 h-px bg-accent" />
            <span className="font-body text-[11px] uppercase tracking-[0.35em] text-accent">
              {data.eyebrow}
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <h2
              data-testid="partnership-title"
              className="col-span-8 font-heading uppercase text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9]"
            >
              {data.title}
            </h2>
            <p className="col-span-4 font-body text-base md:text-lg text-white/60 leading-relaxed">
              {data.description}
            </p>
          </div>
        </motion.div>

        {/* Bento grid: image spotlight + feature cards */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
          {/* Spotlight image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 lg:row-span-2 relative overflow-hidden border border-white/10 group aspect-[4/5] lg:aspect-auto lg:min-h-[680px]"
          >
            <img
              src={data.image}
              alt="DualSense controller"
              className="w-full h-full object-cover transition-transform duration-[2s] ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_60%,rgba(250,204,21,0.15),transparent_50%)]" />
            <div className="absolute bottom-8 left-8 right-8 flex items-end justify-between gap-6">
              <div>
                <div className="font-body text-[10px] uppercase tracking-[0.35em] text-accent mb-3">
                  Console in evidenza
                </div>
                <div className="font-heading text-3xl md:text-5xl uppercase text-white">
                  Xbox Series X|S
                </div>
              </div>
              <div className="hidden md:flex items-center gap-2 font-body text-[10px] uppercase tracking-[0.3em] text-white/60 border border-white/20 px-3 py-2">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                Certificato
              </div>
            </div>
          </motion.div>

          {/* Feature cards */}
          {data.features.map((f, i) => {
            const Icon = ICONS[f.id] || Zap;
            return (
              <motion.div
                key={f.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`feature-card-${f.id}`}
                className="lg:col-span-5 xl:col-span-5 group relative border border-white/10 bg-[#09090B] p-8 md:p-10 hover:border-white/30 transition-colors duration-500 overflow-hidden"
              >
                <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-accent/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="flex items-start justify-between mb-8">
                  <span className="font-heading text-6xl text-white/10 group-hover:text-accent/40 transition-colors duration-500">
                    {f.id}
                  </span>
                  <Icon size={22} className="text-white/40 group-hover:text-accent transition-colors duration-500" />
                </div>
                <h3 className="font-heading text-2xl md:text-3xl uppercase text-white mb-3">
                  {f.title}
                </h3>
                <p className="font-body text-sm md:text-base text-white/60 leading-relaxed">
                  {f.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
